import equal from "fast-deep-equal"
import type Quill from "quill"
import History, { getLastChangeIndex } from "quill/modules/history"
import type Delta from "quill-delta"
import type { MessageContent } from "../types"
import type { EditorRange } from "./sourceTokens"

export type TextSource = Pick<MessageContent, "text" | "formatting">
export const editorHistoryOptions = { ...History.DEFAULTS, userOnly: true }
export const textSource = ({
  text,
  formatting,
}: MessageContent): TextSource => ({
  text,
  formatting,
})

type Entry = Quill["history"]["stack"]["undo"][number]
interface Snapshot {
  before: TextSource
  after: TextSource
  beforeRange: EditorRange | null
  afterRange: EditorRange | null
}
export interface CommandSelection {
  before?: EditorRange | null
  after?: (content: MessageContent) => EditorRange | null
}

/** One bounded Quill stack, with source-authoritative replay. Display inverses
 * alone cannot restore markup spelling, opaque metadata or sidecar-only edits. */
export function installEditorHistory(
  q: Quill,
  getContent: () => MessageContent,
  restore: (content: MessageContent) => void,
  publish: (state: { canUndo: boolean; canRedo: boolean }) => void,
  clearCachedViews: () => void,
) {
  const history = q.history
  const snapshots = new WeakMap<Entry, Snapshot>()
  const originalRecord = history.record
  const originalChange = history.change
  const originalClear = history.clear
  let pending: {
    source: TextSource
    before: TextSource
    display: Delta
    range: EditorRange | null
    entry?: Entry
    stack: typeof history.stack
    lastRecorded: number
  } | null = null
  const refresh = () =>
    publish({
      canUndo: history.stack.undo.length > 0,
      canRedo: history.stack.redo.length > 0,
    })
  const clamp = (range: EditorRange): EditorRange => {
    const end = q.getLength() - 1
    const index = Math.max(0, Math.min(range.index, end))
    return { index, length: Math.max(0, Math.min(range.length, end - index)) }
  }
  const select = (range: EditorRange) => {
    const next = clamp(range)
    q.setSelection(next, "user")
    history.currentRange = next
  }
  const trim = () => {
    if (history.stack.undo.length > editorHistoryOptions.maxStack)
      history.stack.undo.shift()
  }
  history.clear = () => {
    pending = null
    originalClear.call(history)
    refresh()
  }
  history.record = (delta, oldDelta) => {
    const previous = history.stack.undo.at(-1)
    const previousSnapshot = previous && snapshots.get(previous)
    const stack = {
      undo: [...history.stack.undo],
      redo: [...history.stack.redo],
    }
    const lastRecorded = history.lastRecorded
    const range = history.currentRange
    originalRecord.call(history, delta, oldDelta)
    const top = history.stack.undo.at(-1)
    // Quill can eliminate a net-zero display inverse. It must not eliminate a
    // source change, nor accidentally associate it with the preceding entry.
    const entry = top && !stack.undo.includes(top) ? top : undefined
    const merged = previous && !history.stack.undo.includes(previous)
    pending = {
      source: textSource(getContent()),
      before:
        merged && previousSnapshot
          ? previousSnapshot.before
          : textSource(getContent()),
      display:
        merged && previousSnapshot
          ? oldDelta.compose(previous.delta)
          : oldDelta,
      range: merged && previousSnapshot ? previousSnapshot.beforeRange : range,
      entry,
      stack,
      lastRecorded,
    }
  }
  const finishUserChange = () => {
    const change = pending
    pending = null
    if (!change) return
    const after = textSource(getContent())
    if (equal(change.source, after)) {
      // Ignored/no-op display edits do not consume redo or split typing groups.
      history.stack = change.stack
      history.lastRecorded = change.lastRecorded
    } else if (equal(change.before, after)) {
      if (change.entry) history.stack.undo.pop()
    } else {
      const entry = change.entry ?? {
        delta: q.getContents().diff(change.display),
        range: change.range,
      }
      entry.delta = q.getContents().diff(change.display)
      if (!change.entry) history.stack.undo.push(entry)
      snapshots.set(entry, {
        before: change.before,
        after,
        beforeRange: change.range,
        afterRange: history.currentRange && clamp(history.currentRange),
      })
      trim()
    }
    refresh()
  }
  history.change = (source, destination) => {
    if (!q.isEnabled()) return
    const entry = history.stack[source].at(-1)
    if (!entry) return
    const snapshot = snapshots.get(entry)
    const expected = source === "undo" ? snapshot?.after : snapshot?.before
    if (!snapshot || !equal(expected, textSource(getContent()))) {
      // Never fall back to lossy Delta replay against an unknown source.
      history.clear()
      history.cutoff()
      clearCachedViews()
      return
    }
    const display = q.getContents()
    const target = source === "undo" ? snapshot.before : snapshot.after
    const range = source === "undo" ? snapshot.beforeRange : snapshot.afterRange
    // Explicit restore also handles empty display Deltas (no text-change event).
    restore({ ...getContent(), ...target })
    history.stack[source].pop()
    const inverse = { delta: q.getContents().diff(display), range }
    history.stack[destination].push(inverse)
    snapshots.set(inverse, snapshot)
    history.cutoff()
    if (range) select(range)
    else
      select({
        index: getLastChangeIndex(q.scroll, display.diff(q.getContents())),
        length: 0,
      })
    refresh()
  }

  // Quill's native Z/beforeinput handlers already route through change(). Add
  // missing platform variants inside its keyboard owner, never on panel inputs.
  for (const modifier of [{ ctrlKey: true }, { metaKey: true }]) {
    q.keyboard.addBinding({ key: ["z", "Z"], ...modifier }, () =>
      history.undo(),
    )
    q.keyboard.addBinding(
      { key: ["z", "Z"], ...modifier, shiftKey: true },
      () => history.redo(),
    )
    q.keyboard.addBinding({ key: ["y", "Y"], ...modifier }, () =>
      history.redo(),
    )
  }
  refresh()
  return {
    refresh,
    finishUserChange,
    command(next: MessageContent, selection: CommandSelection = {}) {
      if (!q.isEnabled() || equal(textSource(next), textSource(getContent())))
        return
      const before = textSource(getContent())
      const display = q.getContents()
      const beforeRange =
        selection.before ?? q.getSelection() ?? history.currentRange
      history.cutoff()
      restore(next)
      const afterRange =
        selection.after?.(next) ?? q.getSelection() ?? beforeRange
      const entry = { delta: q.getContents().diff(display), range: beforeRange }
      history.stack.undo.push(entry)
      history.stack.redo = []
      snapshots.set(entry, {
        before,
        after: textSource(next),
        beforeRange,
        afterRange,
      })
      trim()
      history.cutoff()
      if (afterRange) select(afterRange)
      refresh()
    },
    dispose() {
      history.record = originalRecord
      history.change = originalChange
      history.clear = originalClear
    },
  }
}
